import { useState } from "react";
import API from "@/lib/api";
import { Stethoscope, Search, Loader2, AlertCircle, CheckCircle, RefreshCw } from "lucide-react";

// One-shot diagnostic UI for the ERP market-QR feed. Surfaces exactly what
// `GET /api/admin/diagnostics/erp-market-qr` returns so admins can debug
// "QR not found in TransAsia registry" rejections without touching curl/cookies.
export default function AdminERPDiagnostics() {
  const [qr, setQr] = useState("");
  const [productCode, setProductCode] = useState("");
  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const runDiagnostic = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const params = new URLSearchParams();
      if (qr.trim()) params.set("qr", qr.trim());
      if (productCode.trim()) params.set("product_code", productCode.trim());
      if (refresh) params.set("refresh", "true");
      const { data } = await API.get(`/admin/diagnostics/erp-market-qr?${params.toString()}`);
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Diagnostic failed");
    } finally {
      setLoading(false);
    }
  };

  const StatusPill = ({ tone, children }) => {
    const tones = {
      ok: "bg-emerald-100 text-emerald-700 border-emerald-200",
      warn: "bg-amber-100 text-amber-700 border-amber-200",
      bad: "bg-red-100 text-red-700 border-red-200",
      neutral: "bg-slate-100 text-slate-700 border-slate-200",
    };
    return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tones[tone] || tones.neutral}`}>{children}</span>;
  };

  const renderRaw = (kind, raw) => {
    if (!raw) return null;
    const httpOk = raw.http_status >= 200 && raw.http_status < 300;
    const hasNetErr = !!raw.network_error;
    return (
      <div className="bg-white border rounded-xl p-4 space-y-2" data-testid={`raw-${kind}`}>
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{kind} feed</h4>
          {hasNetErr ? <StatusPill tone="bad">Network error</StatusPill> :
           !httpOk ? <StatusPill tone="bad">HTTP {raw.http_status}</StatusPill> :
           raw.item_count > 0 ? <StatusPill tone="ok">{raw.item_count} entries</StatusPill> :
           <StatusPill tone="warn">Empty</StatusPill>}
        </div>
        <p className="text-[11px] text-gray-500 font-mono truncate">{raw.url}</p>
        {raw.network_error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 font-mono">{raw.network_error}</p>}
        {raw.json_error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2 font-mono">JSON parse error: {raw.json_error}</p>}
        {raw.root_keys && (
          <div className="text-[11px]">
            <span className="text-gray-500">Root keys: </span>
            <span className="font-mono text-gray-800">{(raw.root_keys || []).join(", ") || "—"}</span>
          </div>
        )}
        {raw.body_preview && (
          <details className="text-[11px]">
            <summary className="text-gray-500 cursor-pointer hover:text-gray-700">Body preview (first 600 chars)</summary>
            <pre className="bg-slate-50 border rounded p-2 mt-1 overflow-auto whitespace-pre-wrap max-h-40">{raw.body_preview}</pre>
          </details>
        )}
        {raw.sample_item && (
          <details className="text-[11px]">
            <summary className="text-gray-500 cursor-pointer hover:text-gray-700">Sample item</summary>
            <pre className="bg-slate-50 border rounded p-2 mt-1 overflow-auto whitespace-pre-wrap max-h-40">{JSON.stringify(raw.sample_item, null, 2)}</pre>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-erp-diagnostics">
      <div className="flex items-center gap-3">
        <Stethoscope className="w-6 h-6 text-[#008A99]" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ERP QR Diagnostic</h1>
          <p className="text-sm text-gray-500">Debug why a market QR is being rejected. Pings ERP market-QR feeds and runs a targeted lookup.</p>
        </div>
      </div>

      <div className="bg-white border rounded-2xl p-5 space-y-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-gray-500">QR string (optional)</label>
          <input
            type="text"
            value={qr}
            onChange={e => setQr(e.target.value)}
            placeholder="e.g. 121938*T052626 : 00001*05/2026*04/2028"
            className="mt-1 w-full px-4 py-2.5 border rounded-xl text-sm font-mono focus:outline-none focus:border-[#008A99]"
            data-testid="diag-qr-input"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Product code (optional)</label>
          <input
            type="text"
            value={productCode}
            onChange={e => setProductCode(e.target.value)}
            placeholder="e.g. 121938"
            className="mt-1 w-full px-4 py-2.5 border rounded-xl text-sm font-mono focus:outline-none focus:border-[#008A99]"
            data-testid="diag-product-input"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input type="checkbox" checked={refresh} onChange={e => setRefresh(e.target.checked)} className="accent-[#008A99]" data-testid="diag-refresh-checkbox" />
          Bust 5-min cache &amp; pull fresh from ERP
        </label>
        <button
          onClick={runDiagnostic}
          disabled={loading}
          className="px-5 py-2.5 bg-[#008A99] text-white rounded-xl text-sm font-bold flex items-center gap-2 disabled:opacity-50"
          data-testid="diag-run-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? "Running…" : "Run Diagnostic"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2 text-sm text-red-700" data-testid="diag-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="diag-result">
          <div className="bg-slate-50 border rounded-xl p-4 text-sm space-y-1">
            <p><span className="text-gray-500">ERP base:</span> <span className="font-mono text-gray-800 text-xs break-all">{result.erp_base}</span></p>
            <p>
              <span className="text-gray-500">Indexed:</span>{" "}
              <span className="font-bold text-gray-900">MFG {result.indexed?.mfg_entries ?? "—"}</span> ·{" "}
              <span className="font-bold text-gray-900">TRD {result.indexed?.trd_entries ?? "—"}</span>
            </p>
          </div>

          {renderRaw("MFG", result.raw?.mfg)}
          {renderRaw("TRD", result.raw?.trd)}

          {result.qr_lookup && (
            <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="diag-qr-lookup">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-gray-900">QR Lookup</h4>
                {result.qr_lookup.hit ? <StatusPill tone="ok">Found in ERP</StatusPill> : <StatusPill tone="bad">NOT in ERP</StatusPill>}
              </div>
              <p className="text-[11px] text-gray-500 font-mono">{result.qr_lookup.qr}</p>
              <details className="text-[11px]">
                <summary className="text-gray-500 cursor-pointer hover:text-gray-700">Parsed components</summary>
                <pre className="bg-slate-50 border rounded p-2 mt-1 overflow-auto whitespace-pre-wrap">{JSON.stringify(result.qr_lookup.parsed, null, 2)}</pre>
              </details>
              {result.qr_lookup.hit && (
                <details className="text-[11px]" open>
                  <summary className="text-gray-500 cursor-pointer hover:text-gray-700">ERP hit details</summary>
                  <pre className="bg-emerald-50 border border-emerald-200 rounded p-2 mt-1 overflow-auto whitespace-pre-wrap">{JSON.stringify(result.qr_lookup.hit, null, 2)}</pre>
                </details>
              )}
            </div>
          )}

          {result.product_lookup && (
            <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="diag-product-lookup">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-gray-900">Product Lookup</h4>
                {(result.product_lookup.mfg_qr_count + result.product_lookup.trd_qr_count) > 0
                  ? <StatusPill tone="ok">Found</StatusPill>
                  : <StatusPill tone="warn">Not in ERP master</StatusPill>}
              </div>
              <p className="text-sm">
                Product code <span className="font-mono font-bold">{result.product_lookup.product_code}</span> ·
                MFG QRs: <span className="font-bold">{result.product_lookup.mfg_qr_count}</span> ·
                TRD QRs: <span className="font-bold">{result.product_lookup.trd_qr_count}</span>
              </p>
              {(result.product_lookup.mfg_sample_qrs?.length > 0 || result.product_lookup.trd_sample_qrs?.length > 0) && (
                <details className="text-[11px]" open>
                  <summary className="text-gray-500 cursor-pointer hover:text-gray-700">Sample QRs from ERP for this product</summary>
                  <pre className="bg-slate-50 border rounded p-2 mt-1 overflow-auto whitespace-pre-wrap max-h-40">{JSON.stringify({
                    mfg: result.product_lookup.mfg_sample_qrs,
                    trd: result.product_lookup.trd_sample_qrs,
                  }, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {!result && !loading && !error && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-xs text-sky-800 leading-relaxed space-y-1">
          <p className="flex items-center gap-1 font-semibold"><RefreshCw className="w-3 h-3" /> How to debug a rejected QR</p>
          <p>1. Paste the rejected QR string (or just the product code from the rejection toast).</p>
          <p>2. Click <strong>Run Diagnostic</strong>.</p>
          <p>3. If <span className="font-bold text-red-600">network_error</span> shows up → ERP is unreachable from the production server.</p>
          <p>4. If MFG/TRD return <span className="font-bold">0 entries</span> → ERP feed itself is empty.</p>
          <p>5. If indexed has thousands but the QR &amp; product both show as NOT found → that QR/product simply isn't in ERP's master.</p>
        </div>
      )}
    </div>
  );
}
